import { EditOutlined, CameraOutlined } from "@ant-design/icons";
import { Alert, Button, Typography, Upload } from "antd";
import type { DraftItems } from "./hooks/useDraftItems";
import type { OrderImages } from "./hooks/useOrderImages";
import ImageThumb from "../../components/ImageThumb";
import SparkleIcon from "./SparkleIcon";
import TextField from "./TextField";
import { PHOTO_GUIDANCE, PHOTO_INTRO } from "./constants";

const { Text } = Typography;

export interface ItemsStepProps {
  items: DraftItems;
  images: OrderImages;
}

export default function ItemsStep({ items, images }: ItemsStepProps) {
  const hasImages = images.paths.length > 0;
  const showItemEntry = hasImages || items.items.length > 0;
  const photoButtonClass = `sendin-photo-button ${
    hasImages ? "sendin-secondary-button sendin-small-button" : "sendin-black-button"
  }`;

  const guidance = <Text type="secondary">{showItemEntry ? PHOTO_GUIDANCE : PHOTO_INTRO}</Text>;

  return (
    <section className="sendin-panel">
      <h2>Registrer gjenstander du vil selge</h2>

      {hasImages ? (
        <div className="sendin-order-preview">
          {images.paths.map((path) => (
            <ImageThumb key={path} path={path} onRemove={() => images.remove(path)} />
          ))}
        </div>
      ) : null}

      {showItemEntry ? (
        <div className="sendin-item-entry">
          <div className="sendin-items-list">
            {items.items.map((item, index) => (
              <div className="sendin-item-row" key={index}>
                <TextField
                  label="Beskrivelse av gjenstand"
                  value={item.description}
                  onChange={(value) => items.updateItem(index, value)}
                  placeholder="F.eks. ring eller armbånd"
                  suffix={<EditOutlined />}
                  wide
                />
                <div className="sendin-item-actions">
                  <Button
                    className="sendin-secondary-button sendin-small-button"
                    onClick={() => items.removeItem(index)}
                  >
                    Fjern
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {images.notice ? (
            <Alert
              className={
                images.notice.tone === "suggestions"
                  ? "sendin-analysis-notice sendin-analysis-notice-ai"
                  : "sendin-analysis-notice"
              }
              type="info"
              showIcon
              icon={images.notice.tone === "suggestions" ? <SparkleIcon /> : undefined}
              message={images.notice.text}
            />
          ) : null}

          {hasImages ? (
            <Button
              className="sendin-black-button sendin-small-button sendin-add-item"
              onClick={() => {
                items.addItem();
                images.dismissManualHint();
              }}
              disabled={items.atLimit}
            >
              Legg til ny gjenstand +
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="sendin-order-images">
        {showItemEntry ? null : guidance}
        <div className="sendin-image-actions">
          <Upload
            fileList={images.fileList}
            onChange={({ fileList }) => {
              images.setFileList(fileList);
              void images.upload(fileList);
            }}
            beforeUpload={images.beforeUpload}
            accept="image/jpeg,image/png,image/webp"
            multiple
            maxCount={images.remainingSlots}
            showUploadList={false}
            disabled={images.atLimit || images.busy}
          >
            <Button
              className={photoButtonClass}
              loading={images.uploading}
              disabled={images.atLimit || images.analyzing}
            >
              {hasImages ? "Last opp et nytt bilde" : "Last opp et bilde"}
            </Button>
          </Upload>
          <Button
            className={`${photoButtonClass} sendin-camera-button`}
            icon={<CameraOutlined />}
            iconPosition="end"
            loading={images.uploading}
            disabled={images.atLimit || images.analyzing}
            onClick={images.openCamera}
          >
            {hasImages ? "Ta et nytt bilde" : "Ta et bilde"}
          </Button>
          <input
            ref={images.cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(event) => void images.handleCameraInputChange(event)}
          />
        </div>
        {showItemEntry ? guidance : null}
        {showItemEntry ? null : (
          <Button type="link" className="sendin-manual-entry" onClick={items.startManualEntry}>
            Legg til gjenstander manuelt
          </Button>
        )}
      </div>
    </section>
  );
}
